import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";

import {
  type CreateTriggerRequest,
  type TriggerRecord,
  type UpdateTriggerRequest,
} from "../TriggerRecord";

import { forgetTrigger, storeTrigger } from "./triggerCache";

// The mutations below don't wait for the list's refetch: they settle as soon
// as the server confirms, which the cache write already shows.

export function useCreateTrigger(orgId?: string) {
  const { triggerService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateTriggerRequest) =>
      triggerService.createTrigger(request, resolvedOrgId),
    onSuccess: (record: TriggerRecord) => {
      void storeTrigger(queryClient, resolvedOrgId, record.name, record);
    },
  });
}

export function useUpdateTrigger(orgId?: string) {
  const { triggerService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      name,
      updates,
    }: {
      name: string;
      updates: UpdateTriggerRequest;
    }) => triggerService.updateTrigger(name, updates, resolvedOrgId),
    onSuccess: (record: TriggerRecord, { name }) => {
      void storeTrigger(queryClient, resolvedOrgId, name, record);
    },
  });
}

/**
 * Mutates a trigger's enabled/disabled state when toggle is called
 */
export function useToggleTrigger(
  trigger: TriggerRecord,
  onError: (error: Error) => void,
): { checked: boolean; pending: boolean; toggle: (enabled: boolean) => void } {
  const { triggerService } = useDomainServices();
  const queryClient = useQueryClient();

  const { mutate, isPending, variables } = useMutation({
    mutationFn: (enabled: boolean) =>
      triggerService.setEnabled(trigger.name, enabled, trigger.org_id),
    onSuccess: (record: TriggerRecord) => {
      void storeTrigger(queryClient, trigger.org_id, trigger.name, record);
    },
    onError,
  });
  return {
    checked: isPending ? variables : trigger.enabled,
    pending: isPending,
    toggle: mutate,
  };
}

export function useDeleteTrigger(orgId?: string) {
  const { triggerService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name }: { name: string }) =>
      triggerService.deleteTrigger(name, resolvedOrgId),
    onSuccess: (_, { name }) => {
      void forgetTrigger(queryClient, resolvedOrgId, name);
    },
  });
}
